import React, { useCallback, useState } from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { Record } from 'immutable';

// Hooks
import { useMeetingItemQueryParam } from 'Shared/components/MeetingItemDetailPanel/useMeetingItemQueryParam';

// Constants
import QueryParamConstants from 'Shared/constants/QueryParams.constants';

// Types
import { MeetingItem } from 'Shared/types/MeetingItem.type';

// Utils
import * as MeetingItemUtils from 'Shared/utils/MeetingItem.utils';

// Helpers
import MeetingItemHelpers from 'Shared/model_helpers/MeetingItem.helper';

// Actions
import * as meetingItemActions from 'Shared/actions/MeetingItems.actions';

// Components
import WpOverflowExpand from 'Wpds/components/overflow-expand/WpOverflowExpand';
import WpButton from 'Wpds/components/button/WpButton';
import WpIcon from 'Wpds/components/icon/WpIcon';
import MeetingItemInput from 'Shared/components/MeetingItemInput/MeetingItemInput';
import DueByDropdown from 'Shared/components/DueByDropdown/DueByDropdown';
import RevisitDropDown from '../RevisitDropDown/RevisitDropDown';
import Checkmark from 'Shared/components/Checkmark/Checkmark';
import MeetingItemDetailPanelAttachments from '../attachments/MeetingItemDetailPanelAttachments';
import MeetingItemDetailsPanelActionItemList from '../ActionItemList/MeetingItemDetailsPanelActionItemList';
import AssociatedDiscussionTopic from '../AssociatedDiscussionTopic/AssociatedDiscussionTopic';
import AssignOwnerDropdown from './AssignOwnerDropdown';

// Styles
import './MeetingItemDetailPanelInfo.scss';

const baseClassName = 'MeetingItemDetailPanelInfo';

type Props = {
  meetingItem: Record<MeetingItem>;
  fileUploaderRef?: { current: HTMLInputElement };
};

const MeetingItemDetailPanelInfo = ({ meetingItem, fileUploaderRef }: Props) => {
  const dispatch = useDispatch();
  const [isInputForcedOpen, setIsInputForcedOpen] = useState(false);

  // we use query params to hide some details while in meeting mode
  const showExpandedDetails = useMeetingItemQueryParam({ paramKey: 'showHeader', defaultValue: true });

  const uuid = MeetingItemHelpers.getUuid(meetingItem);
  const dueBy = MeetingItemHelpers.getDueBy(meetingItem);
  const isCompleted = MeetingItemHelpers.getCompleted(meetingItem);
  const isSection = MeetingItemHelpers.getIsSection(meetingItem);
  const itemType = MeetingItemHelpers.getItemType(meetingItem);
  const recurringWorkspaceItem = MeetingItemHelpers.getRcurringWorkspaceItem(meetingItem);
  const hasNonSectionRuleRecurringWorkspaceItem = recurringWorkspaceItem && !recurringWorkspaceItem.is_section_rule;
  const associatedMeetingItemUuid = MeetingItemHelpers.getAssociatedMeetingItemUuid(meetingItem);
  const isDiscussionTopic = MeetingItemUtils.isDiscussionTopic(itemType);
  const isActionItem = MeetingItemUtils.isActionItem(itemType);

  const showAssociatedMeetingItem = isActionItem && associatedMeetingItemUuid;

  const dueByDisplayDate = dueBy && moment.unix(dueBy).format('MMM D');

  const handleChangeCompleteClick = useCallback(() => {
    const data = {
      item: {
        completed: !isCompleted,
      },
      [QueryParamConstants.Api.DATA_FORMAT]: QueryParamConstants.Api.JSONAPI,
    };
    dispatch(meetingItemActions.updateCompleteStatus(uuid, data));
  }, [dispatch, isCompleted, uuid]);

  const handleUpdateBody = useCallback(
    body => {
      return dispatch(meetingItemActions.updateItemBody(uuid, body));
    },
    [dispatch, uuid]
  );

  const handleDateTimeChange = useCallback(
    time => {
      dispatch(meetingItemActions.updateDueBy(uuid, time));
    },
    [dispatch, uuid]
  );

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__body`}>
        <div className={`${baseClassName}__body__checkmark`}>
          <Checkmark isCompleted={isCompleted} onClick={handleChangeCompleteClick} />
        </div>
        <WpOverflowExpand maxHeight="75px" autoOpen={isInputForcedOpen}>
          <div className={`${baseClassName}__body__input`}>
            <MeetingItemInput
              meetingItem={meetingItem}
              handleUpdateBody={handleUpdateBody}
              onClickCallback={() => setIsInputForcedOpen(true)}
            />
          </div>
        </WpOverflowExpand>
      </div>

      {showExpandedDetails && (
        <div className={`${baseClassName}__details`}>
          <div className={`${baseClassName}__details__item`}>
            <p className={`${baseClassName}__details__item__title`}>{isActionItem ? 'Owner' : 'Discussion Leader'}</p>
            <AssignOwnerDropdown meetingItem={meetingItem} />
          </div>
          {!isDiscussionTopic && (
            <div className={`${baseClassName}__details__item`}>
              <p className={`${baseClassName}__details__item__title`}>Due Date</p>
              <DueByDropdown dueByDate={dueBy} handleUpdateDueByDate={handleDateTimeChange}>
                <div>
                  {dueByDisplayDate ? (
                    <WpButton size="sm" variant="tertiary">
                      <WpIcon name="calendar-alt" />
                      {dueByDisplayDate}
                    </WpButton>
                  ) : (
                    <WpButton size="sm" variant="tertiary">
                      <WpIcon name="calendar-plus" />
                      Add Due Date
                    </WpButton>
                  )}
                </div>
              </DueByDropdown>
            </div>
          )}
          {isDiscussionTopic && (!isSection || hasNonSectionRuleRecurringWorkspaceItem) && (
            <div className={`${baseClassName}__details__item`}>
              <p className={`${baseClassName}__details__item__title`}>Revisit</p>
              <RevisitDropDown meetingItem={meetingItem} placement="bottom-start" />
            </div>
          )}
        </div>
      )}

      {/* {isDiscussionTopic && (!isSection || hasNonSectionRuleRecurringWorkspaceItem) && (
        <div className={`${baseClassName}__recurrence-dropdown`}>
          <RevisitDropDown meetingItem={meetingItem} showNewBadge inlineDropDown={false} />
        </div>
      )} */}

      {showAssociatedMeetingItem && (
        <div className={`${baseClassName}__discussion-topic`}>
          <AssociatedDiscussionTopic meetingItem={meetingItem} />
        </div>
      )}

      {showExpandedDetails && (
        <div className={`${baseClassName}__attachments`}>
          <MeetingItemDetailPanelAttachments meetingItem={meetingItem} fileUploaderRef={fileUploaderRef} />
        </div>
      )}

      {showExpandedDetails && (
        <div className={`${baseClassName}__action-items`}>
          <MeetingItemDetailsPanelActionItemList meetingItem={meetingItem} />
        </div>
      )}
    </div>
  );
};

export default MeetingItemDetailPanelInfo;
